import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Loader2, TrendingDown, TrendingUp, Minus, Shield, HeartPulse, Swords } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { GLOW_FILTER, glowTooltipStyle, glowTooltipItemStyle, glowTooltipLabelStyle, themeRgb } from './charts/GlowChartDefs';

const ZONE_COLORS = {
  epaule: '#f59e0b', bas_du_dos: '#ef4444', genou: '#3b82f6', hanche: '#8b5cf6',
  poignet: '#10b981', cheville: '#06b6d4', cou_cervicales: '#ec4899', coude: '#f97316',
};

const ZONE_ICONS = {
  epaule: '💪', bas_du_dos: '🔙', genou: '🦵', hanche: '🦴',
  poignet: '✋', cheville: '🦶', cou_cervicales: '🧣', coude: '💪',
};

const RecoveryJournal = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = (fr, en) => (language === 'fr' ? fr : en);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/injuries/pain-log/history?days=30');
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.zones.length === 0) {
    return null; // No pain logs yet
  }

  // Build unified chart data (dates as keys, zone scores as values)
  const dateMap = {};
  data.zones.forEach(zone => {
    zone.data_points.forEach(pt => {
      if (!dateMap[pt.date]) {
        dateMap[pt.date] = { date: pt.date, label: formatDate(pt.date, language) };
      }
      dateMap[pt.date][zone.zone] = pt.score;
    });
  });
  const chartData = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Card className="bg-card border-border" data-testid="recovery-journal">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-orange-400" />
          {t('Journal de Readaptation (SITREP)', 'Recovery Journal (SITREP)')}
        </CardTitle>
        <CardDescription>
          {t(
            'Courbe de guerison — ta douleur jour par jour. Sous 3/10 pendant 3 jours = Retour au combat.',
            'Healing curve — your daily pain levels. Under 3/10 for 3 days = Return to combat.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Chart */}
        {chartData.length >= 2 ? (
          <div className="h-48 w-full" data-testid="recovery-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                />
                <YAxis
                  domain={[1, 10]}
                  ticks={[1, 3, 5, 7, 10]}
                  tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                />
                <Tooltip
                  cursor={{ stroke: `rgba(${themeRgb('rose')},0.30)`, strokeWidth: 1 }}
                  contentStyle={glowTooltipStyle(themeRgb('rose'))}
                  itemStyle={glowTooltipItemStyle()}
                  labelStyle={glowTooltipLabelStyle(themeRgb('rose'))}
                />
                {/* Combat-ready threshold line */}
                <ReferenceLine
                  y={3}
                  stroke="rgba(16,185,129,0.5)"
                  strokeDasharray="6 3"
                  // Iter. 406.fr.22 — Use `insideTopRight` instead of `right`.
                  // The default `right` position renders the label OUTSIDE the
                  // SVG viewBox, which pushed the parent flex container 41px
                  // past the viewport on iPhone-SE (360px). insideTopRight
                  // keeps the label inside the chart bounds.
                  label={{
                    value: t('Seuil retour', 'Return threshold'),
                    position: 'insideTopRight',
                    fontSize: 9,
                    fill: 'rgba(16,185,129,0.6)',
                  }}
                />
                {data.zones.map(zone => {
                  const zoneColor = ZONE_COLORS[zone.zone] || '#888';
                  // Strip the leading '#' and convert hex → rgb for glow halo.
                  const hex = zoneColor.replace('#', '');
                  const r = parseInt(hex.slice(0, 2), 16) || 244;
                  const g = parseInt(hex.slice(2, 4), 16) || 114;
                  const b = parseInt(hex.slice(4, 6), 16) || 182;
                  const rgb = `${r},${g},${b}`;
                  return (
                    <Line
                      key={zone.zone}
                      type="monotone"
                      dataKey={zone.zone}
                      name={language === 'fr' ? zone.label_fr : zone.label_en}
                      stroke={zoneColor}
                      strokeWidth={3}
                      dot={{ r: 3, fill: zoneColor }}
                      activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, style: { filter: GLOW_FILTER(rgb) } }}
                      style={{ filter: GLOW_FILTER(rgb) }}
                      connectNulls
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="recovery-chart-empty">
            {t(
              'Continue a logger ta douleur quotidiennement pour voir ta courbe de guerison.',
              'Keep logging your daily pain to see your healing curve.'
            )}
          </div>
        )}

        {/* Zone Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="recovery-zone-cards">
          {data.zones.map(zone => {
            const icon = ZONE_ICONS[zone.zone] || '🩹';
            const label = language === 'fr' ? zone.label_fr : zone.label_en;
            const latest = zone.latest_score;
            const isReady = zone.ready_for_combat;
            const trend = zone.trend;

            return (
              <div
                key={zone.zone}
                className={`p-3 rounded-xl border flex items-center gap-3 transition-all ${
                  isReady
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : latest && latest <= 3
                    ? 'border-yellow-500/30 bg-yellow-500/5'
                    : 'border-border bg-secondary/30'
                }`}
                data-testid={`recovery-zone-${zone.zone}`}
              >
                <span className="text-xl">{icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{label}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {latest !== null && (
                      <span className={`text-lg font-heading font-black ${
                        latest <= 3 ? 'text-emerald-400' : latest <= 5 ? 'text-yellow-400' : latest <= 7 ? 'text-orange-400' : 'text-red-400'
                      }`}>
                        {latest}/10
                      </span>
                    )}
                    {trend === 'improving' && <TrendingDown className="w-3.5 h-3.5 text-emerald-400" />}
                    {trend === 'stable' && <Minus className="w-3.5 h-3.5 text-yellow-400" />}
                  </div>
                </div>
                {isReady && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20" data-testid={`combat-ready-${zone.zone}`}>
                    <Swords className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-[9px] text-emerald-300 font-bold uppercase">
                      {t('Pret!', 'Ready!')}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 pt-1">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-px bg-emerald-500/50 border-t border-dashed border-emerald-500/50" />
            <span className="text-[9px] text-muted-foreground">{t('Seuil retour au combat (3/10)', 'Return to combat threshold (3/10)')}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

function formatDate(dateStr, lang) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' });
}

export default RecoveryJournal;
